"use client"

import {
  ExpressShippingOption,
  listExpressCountries,
  payAsGuest,
  quoteExpressShipping,
  selectExpressShipping,
} from "@lib/data/express-checkout"
import ErrorMessage from "@modules/checkout/components/error-message"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Modal from "@modules/common/components/modal"
import { useParams } from "next/navigation"
import { useEffect, useRef, useState } from "react"

type QuickBuyModalProps = {
  currency: string
  close: () => void
  ensureCart: () => Promise<string>
  onFullCheckout?: () => void
}

type Fields = {
  email: string
  firstName: string
  lastName: string
  phone: string
  address1: string
  postalCode: string
  city: string
  province: string
  country: string
  taxId: string
  company: string
}


const inputClass =
  "w-full rounded-md border border-[#888c8c] px-3 py-2 text-sm shadow-[0_1px_2px_rgba(15,17,17,.15)_inset] focus:border-[#e77600] focus:outline-none focus:shadow-[0_0_3px_2px_rgba(228,121,17,.5)]"

/**
 * Guest quick buy: the minimum data to ship the order, then payment on the
 * MONEI page (Bizum or any other method). No account is created.
 */
export default function QuickBuyModal({
  currency,
  close,
  ensureCart,
  onFullCheckout,
}: QuickBuyModalProps) {
  const { countryCode } = useParams() as { countryCode: string }
  const [cartId, setCartId] = useState<string | null>(null)
  const [countries, setCountries] = useState<{ iso_2: string; name: string }[]>([])
  const [fields, setFields] = useState<Fields>({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    address1: "",
    postalCode: "",
    city: "",
    province: "",
    country: countryCode,
    taxId: "",
    company: "",
  })
  const [wantsInvoice, setWantsInvoice] = useState(false)
  const [options, setOptions] = useState<ExpressShippingOption[]>([])
  const [optionId, setOptionId] = useState<string | null>(null)
  const [total, setTotal] = useState<number | null>(null)
  const [quoting, setQuoting] = useState(false)
  const [submitting, setSubmitting] = useState<"bizum" | "all" | null>(null)
  const [error, setError] = useState<string | null>(null)
  const lastQuote = useRef("")

  const money = (minor: number) =>
    (minor / 100).toLocaleString("es-ES", {
      style: "currency",
      currency: currency.toUpperCase(),
    })

  useEffect(() => {
    ensureCart()
      .then(setCartId)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
    listExpressCountries(countryCode).then(setCountries)
  }, [ensureCart, countryCode])

  const set = (name: keyof Fields) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => setFields((f) => ({ ...f, [name]: e.target.value }))

  // Packlink prices depend only on country and postal code.
  const quote = async (country = fields.country, zip = fields.postalCode) => {
    const signature = `${country}:${zip.trim()}`
    if (!cartId || zip.trim().length < 4 || signature === lastQuote.current) {
      return
    }
    lastQuote.current = signature
    setQuoting(true)
    setError(null)
    const res = await quoteExpressShipping({
      cartId,
      countryCode,
      address: { country, zip: zip.trim(), city: fields.city || undefined },
    })
    setQuoting(false)
    if (res.error) {
      lastQuote.current = ""
      setOptions([])
      setOptionId(null)
      setTotal(null)
      setError(res.error)
      return
    }
    setOptions(res.shippingOptions)
    setOptionId(res.shippingOptions[0]?.id ?? null)
    setTotal(res.amount)
  }

  // The customer may type the postal code before the cart is ready.
  useEffect(() => {
    if (cartId) quote()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId])

  const chooseOption = async (id: string) => {
    if (!cartId) return
    setOptionId(id)
    const { amount } = await selectExpressShipping({ cartId, optionId: id })
    setTotal(amount)
  }

  const pay = async (method: "bizum" | "all") => {
    if (!cartId || !optionId) return
    setSubmitting(method)
    setError(null)
    const res = await payAsGuest({
      ...fields,
      taxId: wantsInvoice ? fields.taxId : undefined,
      company: wantsInvoice ? fields.company : undefined,
      cartId,
      countryCode,
      shippingOptionId: optionId,
      method,
    })
    if (res.ok && "redirectUrl" in res) {
      window.location.href = res.redirectUrl
      return
    }
    setError(res.ok ? "No se pudo iniciar el pago." : res.error)
    setSubmitting(null)
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null
    pay(submitter?.value === "all" ? "all" : "bizum")
  }

  const ready = !!cartId && !!optionId && !quoting && !submitting

  return (
    <Modal isOpen close={close} size="medium" data-testid="quick-buy-modal">
      <Modal.Title>Compra rapida sin registro</Modal.Title>
      <form
        onSubmit={onSubmit}
        className="mt-3 flex flex-col gap-y-3 overflow-y-auto max-h-[calc(75vh-72px)] pr-1"
      >
        <p className="text-sm text-[#565959]">
          Solo necesitamos tus datos de envio. Pagas en la pasarela segura de MONEI y te enviamos la
          confirmacion por email.
        </p>
        <div className="grid grid-cols-1 small:grid-cols-2 gap-3">
          <label className="text-sm font-bold small:col-span-2">
            Email
            <input
              type="email"
              required
              autoComplete="email"
              value={fields.email}
              onChange={set("email")}
              className={inputClass}
              data-testid="quick-buy-email"
            />
          </label>
          <label className="text-sm font-bold">
            Nombre
            <input required autoComplete="given-name" value={fields.firstName} onChange={set("firstName")} className={inputClass} data-testid="quick-buy-first-name" />
          </label>
          <label className="text-sm font-bold">
            Apellidos
            <input autoComplete="family-name" value={fields.lastName} onChange={set("lastName")} className={inputClass} data-testid="quick-buy-last-name" />
          </label>
          <label className="text-sm font-bold small:col-span-2">
            Telefono movil <span className="font-normal text-[#565959]">(el de tu Bizum)</span>
            <input
              type="tel"
              required
              autoComplete="tel"
              value={fields.phone}
              onChange={set("phone")}
              className={inputClass}
              data-testid="quick-buy-phone"
            />
          </label>
          <label className="text-sm font-bold small:col-span-2">
            Direccion
            <input required autoComplete="address-line1" placeholder="Calle, numero, piso" value={fields.address1} onChange={set("address1")} className={inputClass} data-testid="quick-buy-address" />
          </label>
          <label className="text-sm font-bold">
            Codigo postal
            <input
              required
              autoComplete="postal-code"
              inputMode="numeric"
              value={fields.postalCode}
              onChange={set("postalCode")}
              onBlur={() => quote()}
              className={inputClass}
              data-testid="quick-buy-postal-code"
            />
          </label>
          <label className="text-sm font-bold">
            Ciudad
            <input required autoComplete="address-level2" value={fields.city} onChange={set("city")} className={inputClass} data-testid="quick-buy-city" />
          </label>
          <label className="text-sm font-bold">
            Provincia <span className="font-normal text-[#565959]">(opcional)</span>
            <input autoComplete="address-level1" value={fields.province} onChange={set("province")} className={inputClass} />
          </label>
          <label className="text-sm font-bold">
            Pais
            <select
              required
              autoComplete="country"
              value={fields.country}
              onChange={(e) => {
                set("country")(e)
                quote(e.target.value)
              }}
              className={inputClass}
              data-testid="quick-buy-country"
            >
              {countries.map((c) => (
                <option key={c.iso_2} value={c.iso_2}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex items-center gap-x-2 text-sm">
          <input
            type="checkbox"
            checked={wantsInvoice}
            onChange={(e) => setWantsInvoice(e.target.checked)}
            data-testid="quick-buy-wants-invoice"
          />
          Necesito factura con NIF (empresa o autonomo)
        </label>
        {wantsInvoice && (
          <div className="grid grid-cols-1 small:grid-cols-2 gap-3">
            <label className="text-sm font-bold">
              NIF / CIF
              <input required value={fields.taxId} onChange={set("taxId")} className={inputClass} data-testid="quick-buy-tax-id" />
            </label>
            <label className="text-sm font-bold">
              Razon social <span className="font-normal text-[#565959]">(opcional)</span>
              <input autoComplete="organization" value={fields.company} onChange={set("company")} className={inputClass} />
            </label>
          </div>
        )}

        <fieldset className="flex flex-col gap-y-2" data-testid="quick-buy-shipping">
          <legend className="text-sm font-bold mb-1">Envio</legend>
          {quoting ? (
            <p className="text-sm text-[#565959]">Calculando envio con Packlink...</p>
          ) : options.length ? (
            options.map((o) => (
              <label key={o.id} className="flex items-center justify-between gap-x-2 text-sm border border-[#d5d9d9] rounded-md px-3 py-2 cursor-pointer has-[:checked]:border-[#e77600] has-[:checked]:bg-[#fcf5ee]">
                <span className="flex items-center gap-x-2">
                  <input
                    type="radio"
                    name="shipping_option"
                    checked={optionId === o.id}
                    onChange={() => chooseOption(o.id)}
                  />
                  {o.label}
                </span>
                <span className="font-bold">{o.amount === 0 ? "GRATIS" : money(o.amount)}</span>
              </label>
            ))
          ) : (
            <p className="text-sm text-[#565959]">Introduce el codigo postal para ver el precio del envio.</p>
          )}
        </fieldset>

        {total !== null && (
          <p className="text-lg flex justify-between border-t border-[#ddd] pt-2" data-testid="quick-buy-total">
            <span>Total (IVA incluido)</span>
            <span className="font-bold text-amz-price">{money(total)}</span>
          </p>
        )}

        <ErrorMessage error={error} data-testid="quick-buy-error" />

        <p className="text-xs text-[#565959]">
          Al pagar aceptas las{" "}
          <LocalizedClientLink href="/legal/condiciones" className="amz-link">
            condiciones de venta
          </LocalizedClientLink>{" "}
          y la{" "}
          <LocalizedClientLink href="/legal/privacidad" className="amz-link">
            politica de privacidad
          </LocalizedClientLink>
          . Tienes 14 dias de desistimiento y 30 dias de devolucion gratuita.
        </p>

        <button
          type="submit"
          value="bizum"
          disabled={!ready}
          className="amz-btn w-full h-11 bg-[#05c3dd] border border-[#04a9c0] text-white font-bold hover:bg-[#04b3cb]"
          data-testid="quick-buy-bizum"
        >
          {submitting === "bizum" ? "Redirigiendo a Bizum..." : "Pagar con Bizum"}
        </button>
        <button
          type="submit"
          value="all"
          disabled={!ready}
          className="amz-btn-yellow w-full h-10"
          data-testid="quick-buy-other"
        >
          {submitting === "all" ? "Redirigiendo a MONEI..." : "Pagar con tarjeta, PayPal u otro metodo"}
        </button>
        {onFullCheckout && (
          <button
            type="button"
            onClick={onFullCheckout}
            className="amz-link text-sm self-center"
            data-testid="quick-buy-full-checkout"
          >
            Prefiero el proceso de compra completo
          </button>
        )}
      </form>
    </Modal>
  )
}
